<template>
  <b-modal
    v-model="visible"
    hide-header
    hide-footer
    size="md"
    modal-class="mc-mobile-datepicker-modal"
    dialog-class="mc-mobile-datepicker-dialog"
    @hidden="$emit('close')"
  >
    <div class="mc-mobile-datepicker">
      <div class="mc-mobile-datepicker__header">
        <h5 class="m-0">Filter Tanggal</h5>
        <i class="icon-outline_close" @click="visible = false"></i>
      </div>
      <div class="mc-mobile-datepicker__body">
        <date-picker
          v-model="selectedDate"
          inline
          type="date"
          :range="range"
          :disabled-date="disabledDate"
          :lang="datePickerLang"
        />

        <div v-if="range" class="mc-mobile-datepicker__range-labels">
          <div class="range-label-item">
            <div class="label">Mulai</div>
            <div class="value">{{ startDateFormatted }}</div>
          </div>
          <div class="range-label-item">
            <div class="label">Berakhir</div>
            <div class="value">{{ endDateFormatted }}</div>
          </div>
        </div>

        <div v-if="showTime" class="mc-mobile-datepicker__time" :class="{ 'is-range': range }">
          <!-- Start Time Picker -->
          <div class="time-spinner-wrapper">
            <div class="time-spinner-container">
              <div class="time-spinner" ref="hourSpinner" @scroll="onHourScroll">
                <div class="time-spinner__item spacer"></div>
                <div
                  v-for="h in hours"
                  :key="'h-' + h"
                  class="time-spinner__item"
                  :class="{ active: selectedHour === h }"
                >
                  {{ formatNumber(h) }}
                </div>
                <div class="time-spinner__item spacer"></div>
              </div>
              <div class="time-spinner-separator">:</div>
              <div class="time-spinner" ref="minuteSpinner" @scroll="onMinuteScroll">
                <div class="time-spinner__item spacer"></div>
                <div
                  v-for="m in minutes"
                  :key="'m-' + m"
                  class="time-spinner__item"
                  :class="{ active: selectedMinute === m }"
                >
                  {{ formatNumber(m) }}
                </div>
                <div class="time-spinner__item spacer"></div>
              </div>
              <div class="time-spinner-highlight"></div>
            </div>
          </div>

          <!-- End Time Picker (only if range) -->
          <div v-if="range" class="time-spinner-wrapper">
            <div class="time-spinner-container">
              <div class="time-spinner" ref="endHourSpinner" @scroll="onEndHourScroll">
                <div class="time-spinner__item spacer"></div>
                <div
                  v-for="h in hours"
                  :key="'eh-' + h"
                  class="time-spinner__item"
                  :class="{ active: selectedEndHour === h }"
                >
                  {{ formatNumber(h) }}
                </div>
                <div class="time-spinner__item spacer"></div>
              </div>
              <div class="time-spinner-separator">:</div>
              <div class="time-spinner" ref="endMinuteSpinner" @scroll="onEndMinuteScroll">
                <div class="time-spinner__item spacer"></div>
                <div
                  v-for="m in minutes"
                  :key="'em-' + m"
                  class="time-spinner__item"
                  :class="{ active: selectedEndMinute === m }"
                >
                  {{ formatNumber(m) }}
                </div>
                <div class="time-spinner__item spacer"></div>
              </div>
              <div class="time-spinner-highlight"></div>
            </div>
          </div>
        </div>

        <div v-if="availableRangeText" class="mc-mobile-datepicker__available">
          <div class="label">Data tersedia:</div>
          <div class="value">{{ availableRangeText }}</div>
        </div>
      </div>
      <div class="mc-mobile-datepicker__footer">
        <b-button variant="primary" block @click="confirm">Terapkan</b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import moment from 'moment'
import datePickerLang from '@/helpers/date-picker-lang'

export default {
  name: 'McMobileDateTimePicker',
  props: {
    value: {
      type: [String, Date, Array],
      default: null,
    },
    disabledDate: {
      type: Function,
      default: () => false,
    },
    showTime: {
      type: Boolean,
      default: true,
    },
    range: {
      type: Boolean,
      default: false,
    },
    availableRangeText: {
      type: String,
      default: '',
    },
  },
  data() {
    let initialDate = this.range ? [new Date(), new Date()] : new Date()
    let initialStartHour = new Date().getHours()
    let initialStartMinute = new Date().getMinutes()
    let initialEndHour = new Date().getHours()
    let initialEndMinute = new Date().getMinutes()

    if (this.value) {
      const parseDate = (val) => {
        const m = moment(val, [
          moment.ISO_8601,
          'YYYY-MM-DDTHH:mm:ss.SSSZ',
          'YYYY-MM-DD HH:mm:ss',
          'DD MMM YYYY HH:mm',
          'YYYY-MM-DD'
        ])
        return m.isValid() ? m.toDate() : null
      }

      if (this.range && Array.isArray(this.value)) {
        const start = parseDate(this.value[0])
        const end = parseDate(this.value[1])
        if (start && end) {
          initialDate = [start, end]
          initialStartHour = start.getHours()
          initialStartMinute = start.getMinutes()
          initialEndHour = end.getHours()
          initialEndMinute = end.getMinutes()
        }
      } else if (!this.range && !Array.isArray(this.value)) {
        const d = parseDate(this.value)
        if (d) {
          initialDate = d
          initialStartHour = d.getHours()
          initialStartMinute = d.getMinutes()
        }
      }
    }

    return {
      visible: true,
      selectedDate: initialDate,
      selectedHour: initialStartHour,
      selectedMinute: initialStartMinute,
      selectedEndHour: initialEndHour,
      selectedEndMinute: initialEndMinute,
      hours: Array.from({ length: 24 }, (_, i) => i),
      minutes: Array.from({ length: 60 }, (_, i) => i),
      isInitializing: true,
    }
  },
  computed: {
    datePickerLang() {
      return datePickerLang()
    },
    startDateFormatted() {
      const date = this.range ? (this.selectedDate?.[0] || new Date()) : (this.selectedDate || new Date())
      return moment(date).format('DD MMM YYYY')
    },
    endDateFormatted() {
      const date = this.range ? (this.selectedDate?.[1] || new Date()) : null
      return date ? moment(date).format('DD MMM YYYY') : ''
    }
  },
  mounted() {
    this.$nextTick(() => {
      // Ensure spinners are ready before scrolling
      setTimeout(() => {
        this.scrollToValue()
        // Wait even longer to ensure any inertial scrolling or event debouncing finishes
        setTimeout(() => {
          this.isInitializing = false
        }, 500)
      }, 150)
    })
  },
  methods: {
    formatNumber(n) {
      return n.toString().padStart(2, '0')
    },
    scrollToValue() {
      const hSpinner = this.$refs.hourSpinner
      const mSpinner = this.$refs.minuteSpinner
      const ehSpinner = this.$refs.endHourSpinner
      const emSpinner = this.$refs.endMinuteSpinner
      
      if (hSpinner) {
        hSpinner.scrollTop = this.selectedHour * 40
      }
      if (mSpinner) {
        mSpinner.scrollTop = this.selectedMinute * 40
      }
      if (ehSpinner) {
        ehSpinner.scrollTop = this.selectedEndHour * 40
      }
      if (emSpinner) {
        emSpinner.scrollTop = this.selectedEndMinute * 40
      }
    },
    onHourScroll: debounce(function (e) {
      if (this.isInitializing) return
      const itemHeight = 40
      const index = Math.round(e.target.scrollTop / itemHeight)
      if (index >= 0 && index < 24) {
        this.selectedHour = index
        this.snapToItem(e.target, index)
      }
    }, 100),
    onMinuteScroll: debounce(function (e) {
      if (this.isInitializing) return
      const itemHeight = 40
      const index = Math.round(e.target.scrollTop / itemHeight)
      if (index >= 0 && index < 60) {
        this.selectedMinute = index
        this.snapToItem(e.target, index)
      }
    }, 100),
    onEndHourScroll: debounce(function (e) {
      if (this.isInitializing) return
      const itemHeight = 40
      const index = Math.round(e.target.scrollTop / itemHeight)
      if (index >= 0 && index < 24) {
        this.selectedEndHour = index
        this.snapToItem(e.target, index)
      }
    }, 100),
    onEndMinuteScroll: debounce(function (e) {
      if (this.isInitializing) return
      const itemHeight = 40
      const index = Math.round(e.target.scrollTop / itemHeight)
      if (index >= 0 && index < 60) {
        this.selectedEndMinute = index
        this.snapToItem(e.target, index)
      }
    }, 100),
    snapToItem(el, index) {
      const itemHeight = 40
      el.scrollTo({ top: index * itemHeight, behavior: 'smooth' })
    },
    confirm() {
      if (this.range) {
        const startDate = moment(this.selectedDate[0] || new Date())
          .hour(this.selectedHour)
          .minute(this.selectedMinute)
          .second(0)
          .millisecond(0)
        
        const endDate = moment(this.selectedDate[1] || new Date())
          .hour(this.selectedEndHour)
          .minute(this.selectedEndMinute)
          .second(0)
          .millisecond(0)
        
        const result = [startDate.toDate(), endDate.toDate()]
        this.$emit('input', result)
        this.$emit('confirm', result)
      } else {
        const finalDate = moment(this.selectedDate)
          .hour(this.selectedHour)
          .minute(this.selectedMinute)
          .second(0)
          .millisecond(0)
        this.$emit('input', finalDate.toDate())
        this.$emit('confirm', finalDate.toDate())
      }
      this.visible = false
    },
  },
}

function debounce(fn, delay) {
  let timeout
  return function (...args) {
    const context = this
    clearTimeout(timeout)
    timeout = setTimeout(() => fn.apply(context, args), delay)
  }
}
</script>

<style lang="scss">
.mc-mobile-datepicker-modal {
  padding-right: 0 !important;
  display: flex !important;
  align-items: flex-end !important;
  justify-content: center;

  .mc-mobile-datepicker-dialog {
    margin: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
  }

  .modal-content {
    border-radius: 24px 24px 0 0;
    overflow: hidden;
    border: none;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15);
  }
  
  .modal-body {
    padding: 0;
  }

  // Animation for bottom sheet
  &.fade.show .modal-dialog {
    transform: translateY(0) !important;
  }
  &.fade .modal-dialog {
    transform: translateY(100%) !important;
    transition: transform 0.3s ease-out !important;
  }
}

.mc-mobile-datepicker {
  background: #fff;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px 10px;
    h5 {
      color: #1a4d8f;
      font-weight: 700;
      font-size: 18px;
    }
    i {
      font-size: 24px;
      color: #99a1b1;
      cursor: pointer;
    }
  }

  &__body {
    padding: 0; // Remove padding to allow full width
    min-height: 500px;
    
    .mx-datepicker {
      width: 100% !important;
      min-height: 405px !important;
    }

    .mx-datepicker-main {
      border: none;
      box-shadow: none;
      width: 100% !important;
    }
    
    .mx-calendar {
      width: 100% !important;
      padding: 10px 20px !important; // Add padding here instead

      // Hide the second calendar in range mode for mobile to save space
      &:nth-child(2) {
        display: none !important;
      }
    }

    .mx-calendar-inline {
      width: 100% !important;
      padding: 0 !important;
    }
    
    .mx-calendar-header {
      padding: 0 10px 0;
      height: 70px;
      .mx-calendar-header-label {
        font-size: 16px;
        font-weight: 600;
        color: #1a4d8f;
      }
      .mx-btn-icon-double-left, .mx-btn-icon-double-right,
      .mx-btn-icon-left, .mx-btn-icon-right {
        color: #99a1b1;
      }
    }

    .mx-calendar-content {
      position: relative;
      z-index: 1;
      .cell {
        height: 52px;
        width: 52px;
        font-size: 14px;
        color: #333;
        
        &.actived {
          background-color: #2e77d1;
          color: #fff;
          border-radius: 8px;
        }
        &.in-range {
          background-color: #edf4fc;
          color: #333;
          border-radius: 0;
        }
        &.today {
          color: #2e77d1;
          font-weight: 700;
        }
        &.disabled {
          color: #ccc;
          background-color: transparent;
        }
        &.not-current-month {
          color: #ccc;
        }
      }

      .mx-table-date {
        width: 100% !important;
        table-layout: fixed !important;
        margin-top: -35px !important;
        th {
          color: #666;
          font-weight: 500;
          padding: 10px 0;
          &:first-child {
            color: #d14d4d; // Sunday red
          }
        }
      }
    }

    // Remove the old mx-table-date block that was outside
  }

  &__range-labels {
    position: relative;
    z-index: 10;
    background: #fff;
    display: flex;
    justify-content: space-around;
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
    
    .range-label-item {
      text-align: center;
      .label {
        font-size: 12px;
        color: #99a1b1;
        margin-bottom: 4px;
      }
      .value {
        font-size: 14px;
        font-weight: 600;
        color: #1a2b4d;
      }
    }
  }

  &__time {
    padding: 20px 0;
    display: flex;
    justify-content: center;
    border-top: 1px solid #f0f0f0;
    margin-top: 0;

    &.is-range {
      justify-content: space-around;
      padding: 20px 10px;
      
      .time-spinner-wrapper {
        flex: 1;
        display: flex;
        justify-content: center;
      }

      .time-spinner-container {
        width: 140px !important;
        margin-top: 0 !important;
      }
      
      .time-spinner-separator {
        margin: 0 10px !important;
      }
    }
  }

  &__available {
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
    .label {
      font-size: 12px;
      color: #99a1b1;
      margin-bottom: 4px;
    }
    .value {
      font-size: 14px;
      color: #1a2b4d;
    }
  }

  &__footer {
    padding: 0 24px 24px;
    .btn-primary {
      background-color: #1a4d8f;
      border-color: #1a4d8f;
      border-radius: 8px;
      height: 48px;
      font-weight: 600;
      width: 100%;
      font-size: 16px;
    }
  }
}

.time-spinner-container {
  margin-top: 50px !important;
  display: flex !important;
  align-items: center !important;
  position: relative !important;
  height: 120px !important;
  width: 180px !important; // Wider
  overflow: hidden !important;
  background: transparent !important;
}

.time-spinner {
  flex: 1 !important;
  height: 100% !important;
  overflow-y: scroll !important;
  scroll-snap-type: y mandatory !important;
  scrollbar-width: none !important;
  position: relative !important;
  z-index: 2 !important;
  &::-webkit-scrollbar {
    display: none !important;
  }

  &__item {
    height: 40px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 18px !important;
    color: #ccc !important;
    scroll-snap-align: center !important;
    transition: all 0.2s !important;
    background: transparent !important;

    &.active {
      color: #1a2b4d !important;
      font-weight: 700 !important;
      font-size: 22px !important;
    }

    &.spacer {
      height: 40px !important;
    }
  }
}

.time-spinner-separator {
  font-size: 22px !important;
  font-weight: 700 !important;
  color: #1a2b4d !important;
  margin: 0 30px !important; // Even more gap
  position: relative !important;
  z-index: 2 !important;
  top: -1px !important;
}

.time-spinner-highlight {
  position: absolute !important;
  top: 40px !important;
  left: 0 !important;
  right: 0 !important;
  height: 40px !important;
  background-color: #edf4fc !important; // Solid light blue
  pointer-events: none !important;
  border-radius: 8px !important;
  z-index: 1 !important;
}
</style>
